import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useOfferByChatId } from './useOfferByChatId';
import { useOpponentByChatId } from './useOpponentByChatId';
import { markChatsAsRead } from '../actions/actionsChatsList';
import { openChatActionModal } from '../actions/chatActionModal';
import { addChatToSelected } from '../actions/chatList';
import {
  markChatAsStarred,
  moveToArchive,
  removeFromArchive,
  removeFromSpam,
  unmarkChatAsStarred,
} from '../actions/chatOperations';
import { TMenuItem } from '../components/ChatList/ChatListContextMenu';
import {
  trackContextMenuDeleteClick,
  trackContextMenuMarkAsStarredClick,
  trackContextMenuMarkAsReadClick,
  trackContextMenuMoveToArchiveClick,
  trackContextMenuRemoveFromArchiveClick,
  trackContextMenuUnblockClick,
  trackContextMenuUnmarkAsStarredClick,
} from '../containers/ChatListContextMenuContainer/tracking';
import { chatListSelector } from '../selectors';
import { TAvailableActions } from '../types/availableActions';
import { EChatAction } from '../types/chatActionModal';
import { ERoute } from '../types/route';
import { useApplicationContext } from '../utils/applicationContext';
import { isStarredFlagEnabled } from '../utils/isStarredFlagEnabled';

type TUseChatListContextMenuActions = {
  unreadCount: number;
  isStarred: boolean;
  availableActions: TAvailableActions[] | undefined;
};

export const useChatListContextMenuActions = (): ((params: TUseChatListContextMenuActions) => TMenuItem[]) => {
  const dispatch = useDispatch();
  const { config } = useApplicationContext();

  const { currentRoute } = useSelector(chatListSelector);

  const getOfferByChatId = useOfferByChatId();
  const getOpponentByChatId = useOpponentByChatId();

  const isStarredEnabled = isStarredFlagEnabled(config);

  const getMenuItems = React.useCallback(
    ({ unreadCount, isStarred, availableActions }: TUseChatListContextMenuActions) => {
      if (currentRoute === ERoute.Messages || currentRoute === ERoute.Unread || currentRoute === ERoute.Starred) {
        const menuItems = [] as TMenuItem[];

        if (isStarredEnabled) {
          const isMarkAsStarredAvailable = availableActions?.includes('markAsStarred');
          const isUnmarkAsStarredAvailable = availableActions?.includes('unmarkAsStarred');

          if (isStarred && isUnmarkAsStarredAvailable) {
            menuItems.push({
              title: 'Убрать из важных',
              handler: (selectedChatId: string) => {
                const offer = getOfferByChatId(selectedChatId);
                const opponentId = getOpponentByChatId(selectedChatId);

                dispatch(unmarkChatAsStarred({ chatId: selectedChatId }));
                trackContextMenuUnmarkAsStarredClick(offer, opponentId);
              },
            });
          }

          if (!isStarred && isMarkAsStarredAvailable) {
            menuItems.push({
              title: 'Отметить важным',
              handler: (selectedChatId: string) => {
                const offer = getOfferByChatId(selectedChatId);
                const opponentId = getOpponentByChatId(selectedChatId);

                dispatch(markChatAsStarred({ chatId: selectedChatId }));
                trackContextMenuMarkAsStarredClick(offer, opponentId);
              },
            });
          }
        }

        menuItems.push({
          title: 'Перенести в архив',
          handler: (selectedChatId: string) => {
            const offer = getOfferByChatId(selectedChatId);
            const opponentId = getOpponentByChatId(selectedChatId);

            dispatch(moveToArchive(selectedChatId));
            trackContextMenuMoveToArchiveClick(offer, opponentId);
          },
        });

        if (unreadCount) {
          menuItems.push({
            title: 'Отметить как прочитанное',
            handler: (selectedChatId: string) => {
              const offer = getOfferByChatId(selectedChatId);
              const opponentId = getOpponentByChatId(selectedChatId);

              dispatch(markChatsAsRead([selectedChatId]));
              trackContextMenuMarkAsReadClick(offer, opponentId);
            },
          });
        }

        menuItems.push({
          title: 'Удалить',
          isColored: true,
          handler: (selectedChatId: string) => {
            const offer = getOfferByChatId(selectedChatId);
            const opponentId = getOpponentByChatId(selectedChatId);

            dispatch(addChatToSelected(selectedChatId));
            dispatch(
              openChatActionModal({
                buttonText: 'Удалить',
                text: 'Точно хотите удалить чат?',
                chatAction: EChatAction.Delete,
              }),
            );
            trackContextMenuDeleteClick(offer, opponentId);
          },
        });

        return menuItems;
      }

      if (currentRoute === ERoute.Spam) {
        return [
          {
            title: 'Разблокировать',
            handler: (selectedChatId: string) => {
              const offer = getOfferByChatId(selectedChatId);
              const opponentId = getOpponentByChatId(selectedChatId);

              dispatch(removeFromSpam(selectedChatId));
              trackContextMenuUnblockClick(offer, opponentId);
            },
          },
        ];
      }

      return [
        {
          title: 'Вернуть',
          handler: (selectedChatId: string) => {
            const offer = getOfferByChatId(selectedChatId);
            const opponentId = getOpponentByChatId(selectedChatId);

            dispatch(removeFromArchive(selectedChatId));
            trackContextMenuRemoveFromArchiveClick(offer, opponentId);
          },
        },
        {
          title: 'Удалить',
          isColored: true,
          handler: (selectedChatId: string) => {
            const offer = getOfferByChatId(selectedChatId);
            const opponentId = getOpponentByChatId(selectedChatId);

            dispatch(addChatToSelected(selectedChatId));
            dispatch(
              openChatActionModal({
                buttonText: 'Удалить',
                text: 'Точно хотите удалить чат?',
                chatAction: EChatAction.Delete,
              }),
            );
            trackContextMenuDeleteClick(offer, opponentId);
          },
        },
      ];
    },
    [currentRoute, isStarredEnabled, getOfferByChatId, getOpponentByChatId, dispatch],
  );

  return getMenuItems;
};
